import { Rule, Field } from 'ngx-angular-query-builder';
import { DashboardIdentifiers } from '../../dashboards/dashboard-identifiers';
import { DiaryDetailTypes } from '../constants/diary-detail-types';
import * as DiaryTypes from '../../common/enums/diaryType';
import { FieldMetadataCollectionBuilder, FilterOperators, FieldMetadata, FieldMetadataCollection, FilterFieldProvider, FilterListItem, FilterAvailabilityCallbacks } from '@common/filter/filtering';
import { DiaryEvent } from '../models/diary-event';
import { getMapCbk, ArrayToFilterListItemBuilder } from '@common/filter/services/array-to-filter-list-item-builder';
import * as moment from 'moment';

export class DiaryEventFilterFieldProvider implements FilterFieldProvider {

  private readonly fieldMetaDataCollection: FieldMetadataCollection;

  private readonly fieldPredicates: FilterAvailabilityCallbacks;



  constructor(
    dashboardIdentifier: string,
    private readonly adminFilterRules: Rule[],
    effectiveProduct: string) {
    this.fieldMetaDataCollection = new FieldMetadataCollection();
    this.fieldPredicates = new FilterAvailabilityCallbacks(dashboardIdentifier, effectiveProduct);
    this.initialiseFields([]);
  }

  public initialiseFields(events: DiaryEvent[]) {

    const asDate = (d: Date): string => moment(d).format('DD/MM/YYYY');

    const mapDate = <TK extends keyof DiaryEvent>(prop: TK) => {
      return getMapCbk((x: DiaryEvent) => x[prop], (x: DiaryEvent) => asDate(x[prop] as Date));
    }

    const fieldMetaDataBuilder = new FieldMetadataCollectionBuilder(this.fieldMetaDataCollection);
    const optionBuilder = new ArrayToFilterListItemBuilder(events);

    optionBuilder.forProperty('ClientParent',
      'ClientParentRef',
      (x: DiaryEvent) => `${x.ClientParentRef} - ${x.ClientParent}`);

    optionBuilder.forProperty('Client',
      'ClientRef',
      (x: DiaryEvent) => `${x.ClientRef} - ${x.Client}`);

    optionBuilder.forProperty('Property',
      'PropertyRef',
      (x: DiaryEvent) => `${x.PropertyRef} - ${x.Property}`);

    optionBuilder.forProperty('HeadLease', 'HeadLeaseRef', (x: DiaryEvent) => x.HeadLeaseRef);
    optionBuilder.forProperty('Lease', 'LeaseRef', (x: DiaryEvent) => x.LeaseRef);

    optionBuilder.forProperty('Unit',
      'UnitRef',
      (x: DiaryEvent) => `${x.UnitRef} - ${x.Unit}`);

    optionBuilder.forProperty('Address', 'StrungAddress', (x: DiaryEvent) => x.StrungAddress);

    optionBuilder.add('EventDate', (x: DiaryEvent) => asDate(x.EventDate), mapDate('EventDate'));
    optionBuilder.add('NoticeDate', (x: DiaryEvent) => asDate(x.EventDate), mapDate('NoticeDate'));
    optionBuilder.add('BringForwardDate', (x: DiaryEvent) => asDate(x.BringForwardDate), mapDate('BringForwardDate'));
    optionBuilder.add('DateCompleted', (x: DiaryEvent) => asDate(x.DateCompleted), mapDate('DateCompleted'));

    optionBuilder.forProperty('EventTypeDescription', 'EventTypeDescription', (x: DiaryEvent) => x.EventTypeDescription);

    const options = optionBuilder.build();
    const mojoHomeDashboardOnly = () => this.fieldPredicates.includedOnDashboardsForMojoDashboardProduct([DashboardIdentifiers.HomeDashboard]);

    fieldMetaDataBuilder.add('Client Parent', 'ClientParentRef', 'number', FilterOperators.Scalar, options.get('ClientParent'), mojoHomeDashboardOnly);

    fieldMetaDataBuilder.add('Client', 'ClientRef', 'number', FilterOperators.Scalar, options.get('Client'), mojoHomeDashboardOnly);

    fieldMetaDataBuilder.add('Property', 'PropertyRef', 'number', FilterOperators.Scalar, options.get('Property'), () => this.fieldPredicates.includedOnDashboard([
      DashboardIdentifiers.HomeDashboard, DashboardIdentifiers.ClientDashboard, DashboardIdentifiers.EstateDashboard
    ]));

    fieldMetaDataBuilder.add('HeadLease',
      'HeadLeaseRef',
      'number',
      FilterOperators.Scalar,
      options.get('HeadLease'),
      () => this.fieldPredicates.includedOnDashboard([
        DashboardIdentifiers.HomeDashboard, DashboardIdentifiers.ClientDashboard,
        DashboardIdentifiers.PropertyDashboard, DashboardIdentifiers.EstateDashboard
      ]));


    fieldMetaDataBuilder.add('Lease',
      'LeaseRef',
      'number',
      FilterOperators.Scalar,
      options.get('Lease'),
      () => this.fieldPredicates.includedOnDashboard([
        DashboardIdentifiers.HomeDashboard, DashboardIdentifiers.ClientDashboard,
        DashboardIdentifiers.PropertyDashboard, DashboardIdentifiers.EstateDashboard
      ]));

    fieldMetaDataBuilder.add('Tenant Name', 'TenantName', 'string', FilterOperators.All, options.get('Tenant Name'));

    fieldMetaDataBuilder.add('Unit',
      'UnitRef',
      'number',
      FilterOperators.Scalar,
      options.get('Unit'),
      () => this.fieldPredicates.includedOnDashboard([
        DashboardIdentifiers.HomeDashboard, DashboardIdentifiers.ClientDashboard,
        DashboardIdentifiers.PropertyDashboard, DashboardIdentifiers.HeadleaseDashboard,
        DashboardIdentifiers.LeaseDashboard, DashboardIdentifiers.EstateDashboard
      ]));

    fieldMetaDataBuilder.add('Address', 'StrungAddress', 'string', FilterOperators.All, options.get('Address'));

    fieldMetaDataBuilder.add('Event Date', 'EventDate', 'date', FilterOperators.DateDefaults, options.get('EventDate'));

    fieldMetaDataBuilder.add('Notice Date', 'NoticeDate', 'date', FilterOperators.DateDefaults, options.get('NoticeDate'));

    fieldMetaDataBuilder.add('Bring Forward Date', 'BringForwardDate', 'date', FilterOperators.DateDefaults, options.get('BringForwardDate'));

    fieldMetaDataBuilder.add('Completed Date', 'DateCompleted', 'date', FilterOperators.DateDefaults, options.get('DateCompleted'));

    const eventTypes = this.getEventTypes();
    const eventType = {
      name: 'Event Type',
      type: 'category',
      operators: FilterOperators.CategoryDefaults,
      options: this.getQueryBuilderCategories(eventTypes)
    };

    fieldMetaDataBuilder.add('Address', 'StrungAddress', 'string', FilterOperators.All, options.get('Address'));

    this.fieldMetaDataCollection.set(eventType.name,
      new FieldMetadata('EventType',
        eventType,
        () => this.fieldPredicates.includedOnDashboard([
          DashboardIdentifiers.HomeDashboard, DashboardIdentifiers.ClientDashboard,
          DashboardIdentifiers.PropertyDashboard, DashboardIdentifiers.HeadleaseDashboard,
          DashboardIdentifiers.LeaseDashboard, DashboardIdentifiers.EstateDashboard
        ])));

    fieldMetaDataBuilder.add('Event Description',
      'EventTypeDescription',
      'string',
      FilterOperators.StringDefaults,
      options.get('EventTypeDescription'), () => this.fieldPredicates.includedOnDashboard([
        DashboardIdentifiers.HomeDashboard, DashboardIdentifiers.ClientDashboard,
        DashboardIdentifiers.PropertyDashboard, DashboardIdentifiers.HeadleaseDashboard,
        DashboardIdentifiers.LeaseDashboard, DashboardIdentifiers.EstateDashboard
      ]));

    const diaryTypes = this.getDiaryTypes();
    const diaryType = {
      name: 'Diary Type',
      type: 'category',
      operators: FilterOperators.CategoryDefaults,
      options: this.getQueryBuilderCategories(diaryTypes)
    };
    this.fieldMetaDataCollection.set(diaryType.name,
      new FieldMetadata('Entity',
        diaryType,
        () => this.fieldPredicates.includedOnDashboard([
          DashboardIdentifiers.HomeDashboard, DashboardIdentifiers.ClientDashboard,
          DashboardIdentifiers.PropertyDashboard, DashboardIdentifiers.HeadleaseDashboard,
          DashboardIdentifiers.LeaseDashboard, DashboardIdentifiers.EstateDashboard
        ])));
  }

  private getEventTypes(): string[] {
    const allTypes = [DiaryDetailTypes.Expiry, DiaryDetailTypes.Option, DiaryDetailTypes.Review, DiaryDetailTypes.Other];
    if (this.adminFilterRules) {
      const adminFilterRule = this.adminFilterRules.find(f => f.field === 'EventType');
      if (adminFilterRule) {
        if (adminFilterRule.operator === 'in' || adminFilterRule.operator === 'equal') {
          return allTypes.filter(t => (adminFilterRule.value as string[]).includes(t));
        } else {
          // operate => not equal || not in
          return allTypes.filter(t => !(adminFilterRule.value as string[]).includes(t));
        }
      }
    }
    return allTypes;
  }

  private getDiaryTypes(): string[] {
    const allTypes = [DiaryTypes.CLIENT, DiaryTypes.PROPERTY, DiaryTypes.UNIT, DiaryTypes.HEADLEASE, DiaryTypes.LEASE];
    if (this.adminFilterRules) {
      const adminFilterRule = this.adminFilterRules.find(f => f.field === 'Entity');
      if (adminFilterRule) {
        if (adminFilterRule.operator === 'in' || adminFilterRule.operator === 'equal') {
          return allTypes.filter(t => (adminFilterRule.value as string[]).includes(t));
        } else {
          // operate => not equal || not in
          return allTypes.filter(t => !(adminFilterRule.value as string[]).includes(t));
        }
      }
    }
    return allTypes;
  }

  private getQueryBuilderCategories(array: Array<string>) {
    let result = [];
    array.forEach((item) => {
      result.push({
        label: item,
        name: item,
        value: item
      });
    });

    return result;
  }

  getFilterFieldsMetadata(): Array<FieldMetadata> {
    return this.fieldMetaDataCollection.availableFieldMetadata();
  }
  getFilterFields(): Array<Field> {
    return this.fieldMetaDataCollection.getFilterFields();
  }

  getFilterField(fieldName: string): Field {
    return this.fieldMetaDataCollection.getFilterField(fieldName);
  }

  getFieldNameFromLabel(label: string): string {
    return this.fieldMetaDataCollection.getFieldNameFromLabel(label);
  }

}
